import { useState } from "react";

import { Loader2, ThumbsUp, Upload } from "lucide-react";

import {
  isDroppedImageUrl,
  setImageDragData,
} from "@/components/fields/image-drag";
import { PhotoUpload } from "@/components/fields/photo-upload";
import { GcsImage } from "@/components/media/gcs-image";
import { Button } from "@/components/ui/button";
import { ImageZoom } from "@/components/ui/shadcn-io/image-zoom";
import { uploadHitlReplacementImage } from "@/pages/tina/items/components/panels/hitl-panel/image-upload";
import type { ResolutionComponentProps } from "@/pages/tina/items/components/panels/hitl-panel/resolution-components";
import { useItemsRepository } from "@/pages/tina/items/hooks/use-items-repository";
import type { ImageLowConfidenceProblem } from "@/types/tina";

export function ImageResolution({
  item,
  hitlContext,
  onResolved,
}: ResolutionComponentProps) {
  const { resolveHitl } = useItemsRepository();
  const problem = hitlContext.problem as ImageLowConfidenceProblem;
  const [submitting, setSubmitting] = useState(false);
  const [replacement, setReplacement] = useState<string | null>(null);

  async function approve() {
    setSubmitting(true);
    try {
      await resolveHitl(item.id, { resolution: { kind: "image.approve" } });
      onResolved();
    } finally {
      setSubmitting(false);
    }
  }

  async function replace() {
    if (!replacement) return;
    setSubmitting(true);
    try {
      // A dragged candidate arrives as its own URL. `type_genres`' ReplaceImage
      // downloads whatever URL it is handed, so uploading the bytes here would
      // add a cross-origin fetch the curator's browser may not be allowed to
      // make — the reference photos come off our CDN, but the chosen image's
      // URL is the source site's. Only bytes (an upload or a paste) need it.
      const imageUrl = isDroppedImageUrl(replacement)
        ? replacement
        : await uploadHitlReplacementImage(item.id, replacement);
      await resolveHitl(item.id, {
        resolution: { kind: "image.replace", image_url: imageUrl },
      });
      onResolved();
    } finally {
      setSubmitting(false);
    }
  }

  // The URL a dragged candidate hands to the drop box. For the chosen image
  // that is its ORIGINAL source URL, never the blob the panel renders it from:
  // a `blob:` URL is scoped to this page and nothing outside it can fetch one.
  const chosenImageUrl = problem.chosen_image?.url ?? null;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap gap-4">
        {(problem.chosen_image?.ref || problem.chosen_image?.url) && (
          <figure
            className="flex flex-col gap-1 text-xs"
            draggable={Boolean(chosenImageUrl)}
            onDragStart={(e) => {
              if (chosenImageUrl) setImageDragData(e, chosenImageUrl);
            }}
          >
            <GcsImage
              eventId={item.id}
              artifactRef={problem.chosen_image.ref}
              fallbackUrl={problem.chosen_image.url ?? undefined}
              alt="Flagged event image"
              className="h-32 w-32 rounded-md border object-cover"
            />
            <figcaption className="text-muted-foreground">
              Chosen{" "}
              {problem.chosen_image.source &&
                `(${problem.chosen_image.source})`}
            </figcaption>
          </figure>
        )}
        {problem.reference_image_urls.map((url) => (
          <figure
            key={url}
            className="flex flex-col gap-1 text-xs"
            draggable
            onDragStart={(e) => setImageDragData(e, url)}
          >
            <ImageZoom className="shrink-0">
              <img
                src={url}
                alt="Artist reference"
                className="h-32 w-32 rounded-md border object-cover"
              />
            </ImageZoom>
            <figcaption className="text-muted-foreground">Reference</figcaption>
          </figure>
        ))}
      </div>

      <p className="text-sm text-muted-foreground">{problem.verdict_reason}</p>

      <PhotoUpload
        value={replacement}
        onChange={setReplacement}
        onDropUrl={setReplacement}
        accept="image/*"
      />
      <p className="-mt-2 text-xs text-muted-foreground">
        Drag any of the images above onto the box to use it instead.
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" disabled={submitting} onClick={approve}>
          {submitting ? (
            <Loader2 className="mr-1 h-4 w-4 animate-spin" />
          ) : (
            <ThumbsUp className="mr-1 h-4 w-4" />
          )}
          Approve chosen image
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={!replacement || submitting}
          onClick={replace}
        >
          <Upload className="mr-1 h-4 w-4" />
          Use uploaded image
        </Button>
      </div>
    </div>
  );
}
